import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField
} from "@mui/material";

import { get, post, deleteEntry, put } from "src/services/httpClient.service";
import { authenticationService } from "src/services/authentication.service";
import PageTitle from "src/components/PageTitle";
import PageTitleWrapper from "src/components/PageTitleWrapper";
import { Helmet } from "react-helmet-async";
import { hasPermission } from "src/components/Permissions/hasPermission";
import { useSnackbar } from "src/components/snackbar/useSnackbar";
import FormDialog from "src/components/DialogBox";

/* =========================
   Interface
========================= */
export interface ThirdPartyApp {
  id: string;
  appName: string;
  appType: string;
  createdDate: string;
  isActive: boolean;
}

const ThirdAppPartyApps = () => {
  const { showSnackbar } = useSnackbar();
  const { vendorId } = authenticationService.getVendorFranchiseIds();
  const APIURL = process.env.REACT_APP_BASE_API_URL;

  const [tableData, setTableData] = useState<ThirdPartyApp[]>([]);
  const [addOpen, setAddOpen] = useState(false);

  /* ===== Watcho State ===== */
  const [watchoConfigId, setWatchoConfigId] = useState<any | null>(null);

  /* ===== Integration Dialog ===== */
  const [integrateDialogOpen, setIntegrateDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<ThirdPartyApp | null>(null);
  const [dynamicFields, setDynamicFields] = useState<any>({});
  const [loading, setLoading] = useState(false);

  /* =========================
     Fetch Apps
  ========================= */
  const fetchApps = async () => {
    try {
      const response = await get(`${APIURL}/api/third-party-apps/findAll?status=ACTIVE`);
      if (response?.data) setTableData(response.data);
    } catch (error) {
      console.error("Error fetching apps", error);
    }
  };

  useEffect(() => {
    fetchApps();
  }, []);

  /* =========================
     Helpers
  ========================= */
  const getFieldsByAppType = (appName: string) => {
    if (appName.toLowerCase().includes("watcho")) {
      return [
        "watcho.auth.user",
        "watcho.auth.password",
        "watcho.auth.url",
        "watcho.entity.id",
        "watcho.secret.key"
      ];
    }
    return ["api.key", "secret"];
  };

  /* =========================
     Open Dialog
  ========================= */
  const handleIntegrateClick = async (row: ThirdPartyApp) => {
    setSelectedApp(row);

    const fields = getFieldsByAppType(row.appName);
    const initialValues: any = {};
    fields.forEach((f) => (initialValues[f] = ""));
    setDynamicFields(initialValues);

    /* ===== Load Existing Watcho Config ===== */
    if (row.appName.toLowerCase().includes("watcho")) {
      try {
        //const res = await get(`${APIURL}/api/watcho-config/active`);
        const res = await get(`${APIURL}/api/vendor-app-integrations/vendor/${vendorId}/type/${row.appType}`);

        if (res?.data) {

          const config = res.data;
          const credentials = JSON.parse(config.appCredentials); // parse JSON string

          setWatchoConfigId(config.id);

          setDynamicFields({
            "watcho.auth.user": credentials.authUser,
            "watcho.auth.password": credentials.authPassword,
            "watcho.auth.url": credentials.authUrl,
            "watcho.entity.id": credentials.entityId,
            "watcho.secret.key": credentials.secretKey
          });
      }

        // if (res?.data?.length > 0) {
        //   const config = res.data[0];

        //   setWatchoConfigId(config.id);

        //   setDynamicFields({
        //     "watcho.auth.user": config.authUser,
        //     "watcho.auth.password": config.authPassword,
        //     //"watcho.entity.type": config.entityType,
        //     "watcho.auth.url": config.authUrl,
        //     "watcho.entity.id": config.entityId,
        //     "watcho.secret.key": config.secretKey
        //   });
        // }
      } catch {
        console.log("No existing Watcho config");
      }
    }

    setIntegrateDialogOpen(true);
  };

  const handleFieldChange = (key: string, value: string) => {
    setDynamicFields((prev: any) => ({ ...prev, [key]: value }));
  };

  /* =========================
     Submit (Create / Update)
  ========================= */
  const handleIntegrateSubmit = async () => {
    if (!selectedApp) return;

    try {
      setLoading(true);

      console.log(selectedApp)

      /* ===== WATCHO FLOW ===== */
      if (selectedApp.appName.toLowerCase().includes("watcho")) {

        const payload = {
          authUrl: dynamicFields["watcho.auth.url"],
          authUser: dynamicFields["watcho.auth.user"],
          authPassword: dynamicFields["watcho.auth.password"],
          //entityType: dynamicFields["watcho.entity.type"],
          entityId: dynamicFields["watcho.entity.id"],
          secretKey: dynamicFields["watcho.secret.key"]
        };

        // if (watchoConfigId) {
        //   await put(`${APIURL}/api/watcho-config/${watchoConfigId}`, payload);
        //   showSnackbar("Watcho config updated successfully", "success");
        // } else {
        //   await post(`${APIURL}/api/watcho-config`, payload);
        //   showSnackbar("Watcho config saved successfully", "success");
        // }

        const integrationPayload = {
          id: watchoConfigId,
          appName: selectedApp.appName,
          appType: selectedApp.appType,
          appCredentials: JSON.stringify(payload),
          vendorId: vendorId,
          status: true
        }

        if (watchoConfigId) {
          await put(`${APIURL}/api/vendor-app-integrations/${watchoConfigId}`, integrationPayload);
          showSnackbar("Config updated successfully", "success");
        } else {
          await post(`${APIURL}/api/vendor-app-integrations`, integrationPayload);
          showSnackbar("Config saved successfully", "success");
        }
      }

      /* ===== OTHER APPS ===== */
      else {
        await post(`${APIURL}/api/vendor-app-integrations/create`, {
          vendorId,
          thirdPartyAppId: selectedApp.id,
          config: dynamicFields
        });

        showSnackbar("App integrated successfully", "success");
      }

      setIntegrateDialogOpen(false);
    } catch (error: any) {
      showSnackbar(error?.message || "Failed", "error");
    } finally {
      setLoading(false);
    }
  };

  /* =========================
     Reset Watcho Config
  ========================= */
  const handleResetWatcho = async () => {
    if (!watchoConfigId) return;

    await deleteEntry(`${APIURL}/api/watcho-config/${watchoConfigId}`);
    showSnackbar("Watcho config removed", "success");

    setWatchoConfigId(null);
    setDynamicFields({});
  };

  return (
    <>
      <Helmet>
        <title>Third Party Apps</title>
      </Helmet>

      <PageTitleWrapper>
        {hasPermission(["OWNER"]) && (
          <PageTitle heading="Third Party Apps" handleClick={() => setAddOpen(true)} />
        )}
      </PageTitleWrapper>

      {addOpen && (
        <FormDialog
          open={addOpen}
          onClose={() => setAddOpen(false)}
          title="Add Application"
          component="AddThirdApp"
          fetchApps={fetchApps}
        />
      )}

      {/* ===== Cards ===== */}
      <Box sx={{ px: 2, pb: 4 }}>
        <Grid container spacing={2}>
          {tableData.map((app) => (
            <Grid item xs={12} sm={6} md={4} key={app.id}>
              <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
                <CardContent sx={{ textAlign: "center" }}>
                  <Typography variant="h6" fontWeight="bold">
                    {app.appName}
                  </Typography>

                  <Typography mt={1}>Type: {app.appType}</Typography>

                  <Button
                    fullWidth
                    variant="contained"
                    sx={{ mt: 2 }}
                    onClick={() => handleIntegrateClick(app)}
                  >
                    Integrate
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* ===== Integration Dialog ===== */}
      <Dialog open={integrateDialogOpen} fullWidth maxWidth="sm">
        <DialogTitle>Integrate {selectedApp?.appName}</DialogTitle>

        <DialogContent>
          {Object.keys(dynamicFields).map((key) => (
            <TextField
              key={key}
              fullWidth
              margin="normal"
              label={key}
              value={dynamicFields[key]}
              onChange={(e) => handleFieldChange(key, e.target.value)}
            />
          ))}
        </DialogContent>

        <DialogActions>
          {watchoConfigId && (
            <Button color="error" onClick={handleResetWatcho}>
              Reset Config
            </Button>
          )}

          <Button onClick={() => setIntegrateDialogOpen(false)}>Cancel</Button>

          <Button variant="contained" onClick={handleIntegrateSubmit}>
            {loading ? <CircularProgress size={20} /> : "Submit"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ThirdAppPartyApps;